// src/pages/AboutPage.js
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FiUsers, FiTarget, FiEye, FiCheckCircle, FiFacebook, FiInstagram } from "react-icons/fi";
import { SiTiktok } from "react-icons/si";

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-theme-bg">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[150%] bg-chafadi-primary/20 blur-[120px] rounded-full" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <img src="/logo.png" alt="Chafadi" className="h-16 w-auto mx-auto mb-10" />
          <h1 className="text-5xl md:text-7xl font-black text-theme-text tracking-tight mb-6 font-display">
            {t("aboutPage.title")}
          </h1>
          <p className="text-xl md:text-2xl text-theme-muted font-medium max-w-3xl mx-auto leading-relaxed">
            Connecting Turkish Suppliers with African Buyers. Bridging continents with a next-gen trade infrastructure.
          </p>
        </div>
      </section>

      {/* Values Grid */}
      <section className="max-w-7xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: <FiUsers />,
              title: t("aboutPage.whoWeAre"),
              text: t("aboutPage.whoWeAreText"),
              color: "bg-blue-50 text-blue-600",
            },
            {
              icon: <FiTarget />,
              title: t("aboutPage.ourMission"),
              text: t("aboutPage.ourMissionText"),
              color: "bg-orange-50 text-chafadi-primary",
            },
            {
              icon: <FiEye />,
              title: t("aboutPage.ourVision"),
              text: t("aboutPage.ourVisionText"),
              color: "bg-emerald-50 text-emerald-600",
            },
          ].map((item, i) => (
            <div key={i} className="p-10 rounded-[40px] border border-component-border bg-white shadow-sm hover:shadow-xl transition-all duration-500 group">
              <div className={`w-16 h-16 rounded-2xl ${item.color} flex items-center justify-center text-3xl mb-8 group-hover:scale-110 transition-transform duration-500`}>
                {item.icon}
              </div>
              <h3 className="text-2xl font-black text-theme-text mb-4 uppercase tracking-tight">{item.title}</h3>
              <p className="text-theme-muted font-medium leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social Section */}
      <section className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-black uppercase tracking-[0.2em] text-[#ff7a00] mb-12">Follow Our Story</h2>
        <div className="flex justify-center gap-8">
          <a 
            href="https://www.tiktok.com/@chafadi2?_r=1&_t=ZS-95drU5V5WMC" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex flex-col items-center gap-4 group"
          >
            <div className="w-20 h-20 rounded-[30px] bg-gray-50 flex items-center justify-center text-3xl text-gray-400 border border-gray-100 group-hover:bg-[#ff7a00] group-hover:text-white group-hover:-translate-y-2 transition-all duration-500 shadow-sm group-hover:shadow-xl group-hover:shadow-orange-500/20">
              <SiTiktok />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-[#ff7a00]">TikTok</span>
          </a>

          <a 
            href="https://www.facebook.com/share/1AVc5SWEcG/?mibextid=wwXIfr" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex flex-col items-center gap-4 group"
          >
            <div className="w-20 h-20 rounded-[30px] bg-gray-50 flex items-center justify-center text-3xl text-gray-400 border border-gray-100 group-hover:bg-[#ff7a00] group-hover:text-white group-hover:-translate-y-2 transition-all duration-500 shadow-sm group-hover:shadow-xl group-hover:shadow-orange-500/20">
              <FiFacebook />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-[#ff7a00]">Facebook</span>
          </a>

          <a 
            href="https://www.instagram.com/chafadicom?igsh=NnBrMTFna2l4a2th&utm_source=qr" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex flex-col items-center gap-4 group"
          >
            <div className="w-20 h-20 rounded-[30px] bg-gray-50 flex items-center justify-center text-3xl text-gray-400 border border-gray-100 group-hover:bg-[#ff7a00] group-hover:text-white group-hover:-translate-y-2 transition-all duration-500 shadow-sm group-hover:shadow-xl group-hover:shadow-orange-500/20">
              <FiInstagram />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 group-hover:text-[#ff7a00]">Instagram</span>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-5xl mx-auto px-4 pb-32 sm:px-6 lg:px-8">
        <div className="bg-theme-text rounded-[60px] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-chafadi-primary/20 blur-[80px] rounded-full translate-x-1/2 -translate-y-1/2" />
          
          <div className="relative z-10 space-y-10">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                {t("aboutPage.areYouACompany")}
              </h2>
              <p className="text-white/60 text-lg font-medium max-w-2xl mx-auto">
                {t("aboutPage.joinUsText")}
              </p>
            </div>

            <div className="flex flex-col items-center gap-6">
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-3xl">
                {[1, 2, 3].map(i => (
                  <li key={i} className="flex items-center gap-3 bg-white/5 backdrop-blur-md px-6 py-4 rounded-3xl border border-white/10">
                    <FiCheckCircle className="text-chafadi-primary shrink-0" size={20} />
                    <span className="text-sm font-black text-white uppercase tracking-widest text-left leading-tight">
                      {t(`aboutPage.joinUsStep${i}`)}
                    </span>
                  </li>
                ))}
              </ul>

              <Link 
                to="/login" 
                className="inline-flex items-center h-16 px-12 bg-chafadi-primary text-white font-black rounded-full shadow-2xl shadow-orange-500/20 hover:scale-105 active:scale-95 transition-all text-sm uppercase tracking-[0.2em] pt-1"
              >
                {t("aboutPage.getStarted")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
